import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'

export type LayoutShareStatus = 'active' | 'pending'

export interface ActiveCollaborator {
  status: 'active'
  recipientUserId: string
  recipientEmail: string
  role: 'viewer' | 'editor'
  grantedAt: string
  expiresAt?: string
}

export interface PendingCollaborator {
  status: 'pending'
  recipientEmail: string
  role: 'viewer' | 'editor'
  invitedAt: string
  expiresAt: string
}

export type Collaborator = ActiveCollaborator | PendingCollaborator

interface CollaboratorsResponse {
  collaborators: Collaborator[]
}

interface ShareResponse {
  status: LayoutShareStatus
  layoutId: string
  recipientEmail: string
  role: 'viewer' | 'editor'
  // Present on active shares (recipient already had an account).
  recipientUserId?: string
  grantedAt?: string
  // Present on pending invites (WorkOS invitation sent).
  invitedAt?: string
  expiresAt?: string
}

export function useCollaborators(layoutId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['layout-collaborators', layoutId],
    queryFn: async () => {
      if (!layoutId) return [] as Collaborator[]
      const response = await apiClient.get<CollaboratorsResponse>(
        `/api/v1/layouts/${layoutId}/collaborators`,
      )
      return response.collaborators ?? []
    },
    enabled: enabled && !!layoutId,
  })
}

export function useShareLayout(layoutId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<
    ShareResponse,
    Error,
    { email: string; role: 'viewer' | 'editor'; expiresAt?: string }
  >({
    mutationFn: async input => {
      if (!layoutId) throw new Error('No layoutId')
      return apiClient.post<ShareResponse>(
        `/api/v1/layouts/${layoutId}/share`,
        input,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['layout-collaborators', layoutId],
      })
      queryClient.invalidateQueries({ queryKey: ['layouts'] })
    },
  })
}

export function useUnshareLayout(layoutId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (recipientUserId: string) => {
      if (!layoutId) throw new Error('No layoutId')
      return apiClient.delete(
        `/api/v1/layouts/${layoutId}/share/${recipientUserId}`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['layout-collaborators', layoutId],
      })
      queryClient.invalidateQueries({ queryKey: ['layouts'] })
    },
  })
}

/**
 * Revoke a pending invitation (the recipient has not yet accepted).
 * The backend also calls WorkOS `revokeInvitation` so the email
 * link the recipient received becomes inert.
 */
export function useRevokeInvite(layoutId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (email: string) => {
      if (!layoutId) throw new Error('No layoutId')
      return apiClient.delete(
        `/api/v1/layouts/${layoutId}/invite/${encodeURIComponent(email)}`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['layout-collaborators', layoutId],
      })
      queryClient.invalidateQueries({ queryKey: ['layouts'] })
    },
  })
}
